import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { AgentStatus, IAgent } from '@/store/types';

import { Table } from '@/components/common';
import { getContractDisplayLabel } from '@/helpers/client';
import { NoAgents } from '@/assets/icons';
import { NoData } from '@/components/agent/dashboard';
import { formatBlock } from '@/helpers/dateFormatter';
import { useTimezone } from '@/hooks';

const tableConfig = {
  contractName: { width: '25%' },
  contractStartDate: { width: '13%' },
  contactEndDate: { width: '13%' },
  status: { width: '13%' },
  reason: { width: '13%' },
  effectiveDate: { width: '13%' },
};

const getAgentStatus = (statuses: AgentStatus[]): AgentStatus | undefined => {
  if (statuses.length === 1) return statuses[0];
  if (statuses.includes('active')) return 'reinstated';
  return;
};

const HistoryTable = ({ history }: { history: IAgent[] }): React.ReactElement => {
  const { t } = useTranslation();
  const { timezoneName } = useTimezone();

  const actualStatus = (agent: IAgent) => getAgentStatus(agent.agentStatuses);

  const agentRow = useCallback(
    (agent: IAgent, index: number) => (
      <Table.TableRow key={`${agent.contractId}-${index}`} firstBgColor="#f3f4f6" secondBgColor="#fff">
        <Table.Cell width={tableConfig.contractName.width} multiline={2}>
          {getContractDisplayLabel(agent)}
        </Table.Cell>
        <Table.Cell width={tableConfig.contractStartDate.width} multiline={2}>
          {formatBlock(agent.contractAssnStartBlock, timezoneName)}
        </Table.Cell>
        <Table.Cell width={tableConfig.contactEndDate.width} multiline={2}>
          {formatBlock(agent.contractAssnEndBlock, timezoneName)}
        </Table.Cell>
        <Table.Cell width={tableConfig.status.width} multiline={2}>
          {actualStatus(agent)
            ? t(`clientGigs.historyTable.status.${actualStatus(agent)}`)
            : t`clientGigs.historyTable.status.unknown`}
        </Table.Cell>
        <Table.Cell width={tableConfig.reason.width} multiline={2}>
          {agent.restermReasonLabel}
        </Table.Cell>
        <Table.Cell width={tableConfig.effectiveDate.width} multiline={2}>
          {formatBlock(agent.effectiveBlock, timezoneName)}
        </Table.Cell>
      </Table.TableRow>
    ),
    [timezoneName, t],
  );

  if (!history.length)
    return (
      <Table minWidth={900}>
        <NoData icon={NoAgents} text={t`clientGigs.historyTable.noData`} />
      </Table>
    );

  return (
    <Table minWidth={900} type="scrolling">
      <Table.Header>
        <Table.HeaderCell
          width={tableConfig.contractName.width}
        >{t`clientGigs.historyTable.label.gigName`}</Table.HeaderCell>
        <Table.HeaderCell
          width={tableConfig.contractStartDate.width}
        >{t`clientGigs.historyTable.label.gigStartDate`}</Table.HeaderCell>
        <Table.HeaderCell
          width={tableConfig.contactEndDate.width}
        >{t`clientGigs.historyTable.label.contactEndDate`}</Table.HeaderCell>
        <Table.HeaderCell width={tableConfig.status.width}>{t`clientGigs.historyTable.label.status`}</Table.HeaderCell>
        <Table.HeaderCell width={tableConfig.reason.width}>{t`clientGigs.historyTable.label.reason`}</Table.HeaderCell>
        <Table.HeaderCell
          width={tableConfig.effectiveDate.width}
        >{t`clientGigs.historyTable.label.effectiveDate`}</Table.HeaderCell>
      </Table.Header>
      {history.map(agentRow)}
    </Table>
  );
};

export { HistoryTable };
